/**
 * ⚠️ FORKED COMPONENT — DO NOT MERGE BACK ⚠️
 *
 * LR DIRECT — Broker side of private network invitations.
 *
 * Two modes:
 *  1. URL has `?token=…` → fetch invitation details via edge function and show
 *     an accept/decline confirm card (works for invitees who clicked an email link).
 *  2. No token → list all pending invitations for the logged-in broker, with
 *     accept/decline buttons.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { purchasesEnabled } from "@/lib/billing/purchaseGate";
import {
  Inbox, Building2, Clock, CheckCircle2, XCircle, ArrowLeft, Mail, Shield, Loader2,
} from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { markJoinRequestResponsesRead } from "@/hooks/useUnreadJoinRequestResponses";

interface InviteRow {
  id: string;
  token: string;
  invited_email: string;
  message: string | null;
  status: string;
  expires_at: string;
  created_at: string;
  network_id: string;
  shipper_user_id: string;
  // enriched
  shipperCompanyName?: string | null;
  networkName?: string;
}

interface TokenInviteDetails {
  invitedEmail: string;
  message: string | null;
  networkName: string;
  shipperCompanyName: string | null;
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function LRDirectNetworkInvitationsPage() {
  usePageMeta({
    title: "LR Direct — Network Invitations | LaneRunner",
    description: "Accept or decline shipper invitations to join their private broker network.",
  });

  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tokenParam = searchParams.get("token");

  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenDetails, setTokenDetails] = useState<TokenInviteDetails | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [tokenResponding, setTokenResponding] = useState<"accept" | "decline" | null>(null);

  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [responding, setResponding] = useState<Record<string, "accept" | "decline" | undefined>>({});
  const [myRequests, setMyRequests] = useState<Array<{ id: string; status: string; message: string | null; requested_at: string; decided_at: string | null; shipper_company_id: string; shipper_name: string | null }>>([]);

  const isProvider =
    profile?.user_type === "broker" ||
    profile?.user_type === "broker_carrier" ||
    profile?.user_type === "carrier";

  /* ── Token-mode fetch ─────────────────────────────────────────── */
  useEffect(() => {
    if (!tokenParam) {
      setTokenDetails(null);
      setTokenError(null);
      return;
    }
    let cancelled = false;
    setTokenLoading(true);
    setTokenError(null);
    (async () => {
      try {
        const SUPABASE_URL = (import.meta as any).env.VITE_SUPABASE_URL;
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/accept-network-invitation?token=${encodeURIComponent(tokenParam)}`,
          { method: "GET" }
        );
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setTokenError(json?.error || "Could not load invitation");
          setTokenDetails(null);
        } else {
          setTokenDetails(json as TokenInviteDetails);
        }
      } catch (e: any) {
        if (!cancelled) setTokenError(e?.message || "Network error");
      } finally {
        if (!cancelled) setTokenLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tokenParam]);

  /* ── List-mode fetch (pending invites for this broker) ───────── */
  const loadList = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    // Email-based fallback: claim any pending invitation addressed to this
    // broker's email that isn't linked to their account yet (token lost /
    // invite predates the signup trigger). Stamps invited_broker_user_id so
    // the rows below become RLS-readable. Idempotent, safe to call every load.
    try {
      await (supabase as any).rpc("claim_my_network_invites");
    } catch { /* non-fatal — fall through to whatever is already linked */ }


    const { data: rows, error } = await supabase
      .from("shipper_network_invitations" as any)
      .select("id, token, invited_email, message, status, expires_at, created_at, network_id, shipper_user_id")
      .eq("invited_broker_user_id", user.id)
      .eq("status", "pending")
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) {
      toast.error(error.message);
      setLoading(false);
      return;
    }

    const list = ((rows as any[]) || []) as InviteRow[];

    // Enrich with shipper company name via the public GET edge function (no auth needed)
    const SUPABASE_URL = (import.meta as any).env.VITE_SUPABASE_URL;
    const enriched = await Promise.all(
      list.map(async (inv) => {
        try {
          const res = await fetch(
            `${SUPABASE_URL}/functions/v1/accept-network-invitation?token=${encodeURIComponent(inv.token)}`,
            { method: "GET" }
          );
          if (!res.ok) return inv;
          const j = await res.json();
          return {
            ...inv,
            shipperCompanyName: j.shipperCompanyName ?? null,
            networkName: j.networkName ?? "Private Network",
          };
        } catch {
          return inv;
        }
      })
    );

    setInvites(enriched);

    // Load my outgoing join-requests
    const { data: reqRows } = await supabase
      .from("shipper_network_join_requests" as any)
      .select("id, status, message, requested_at, decided_at, shipper_company_id")
      .eq("broker_user_id", user.id)
      .order("requested_at", { ascending: false })
      .limit(25);
    const reqs = ((reqRows as any[]) || []);
    const shipperIds = Array.from(new Set(reqs.map((r) => r.shipper_company_id))).filter(Boolean);
    let nameMap = new Map<string, string>();
    if (shipperIds.length) {
      const { data: cos } = await supabase.from("companies").select("id, name").in("id", shipperIds);
      ((cos as any[]) || []).forEach((c) => nameMap.set(c.id, c.name));
    }
    setMyRequests(reqs.map((r) => ({ ...r, shipper_name: nameMap.get(r.shipper_company_id) ?? null })));

    setLoading(false);
  }, [user]);

  useEffect(() => { loadList(); }, [loadList]);

  // Mark accepted/declined join-request notifications as read when broker opens this page.
  useEffect(() => {
    if (!user?.id) return;
    markJoinRequestResponsesRead(user.id).catch(() => { /* noop */ });
  }, [user?.id]);

  /* ── Respond helpers ─────────────────────────────────────────── */
  const callRespond = async (token: string, action: "accept" | "decline") => {
    const { data, error } = await supabase.functions.invoke("accept-network-invitation", {
      body: { token, action },
    });
    if (error) throw error;
    if ((data as any)?.requires_subscription) {
      const err: any = new Error((data as any).error || "LR Direct Private subscription required");
      err.requiresSubscription = true;
      throw err;
    }
    // Email mismatch and already-a-member are handled by the UI; don't throw.
    if ((data as any)?.error && !(data as any)?.email_mismatch && !(data as any)?.already_member) {
      throw new Error((data as any).error);
    }
    return data;
  };

  const handleSubscriptionPrompt = () => {
    if (!purchasesEnabled()) {
      toast.message("Subscription required", {
        description: "This network requires a paid plan. Contact support at support@lanerunner.com for help with your account.",
        duration: 12000,
      });
      return;
    }
    toast.message("Subscription required", {
      description: "Subscribe to LR Direct Private to join this network. Your invitation will accept automatically after payment.",
      action: {
        label: "Subscribe",
        onClick: () => navigate("/pricing?tier=lr_direct_private"),
      },
      duration: 12000,
    });
  };

  const showMismatchMessage = () => {
    toast.error("This invite was sent to a different address", {
      description: "To join this network, go to My Network → click Find a Shipper → add the network key → request to join.",
      duration: 15000,
    });
  };

  const handleListRespond = async (inv: InviteRow, action: "accept" | "decline") => {
    setResponding((r) => ({ ...r, [inv.id]: action }));
    try {
      const data = await callRespond(inv.token, action);
      if ((data as any)?.email_mismatch) {
        showMismatchMessage();
      } else if ((data as any)?.already_member) {
        toast.success("You're already a member of this network — you have full access.");
      } else {
        toast.success(action === "accept" ? "You joined the network" : "Invitation declined");
      }
      await loadList();
    } catch (e: any) {
      if (e?.requiresSubscription) handleSubscriptionPrompt();
      else toast.error(e?.message || `Failed to ${action}`);
    } finally {
      setResponding((r) => ({ ...r, [inv.id]: undefined }));
    }
  };

  const handleTokenRespond = async (action: "accept" | "decline") => {
    if (!tokenParam) return;
    setTokenResponding(action);
    try {
      const data = await callRespond(tokenParam, action);
      if ((data as any)?.email_mismatch) {
        showMismatchMessage();
      } else if ((data as any)?.already_member) {
        toast.success("You're already a member of this network — you have full access.");
      } else {
        toast.success(action === "accept" ? "You joined the network" : "Invitation declined");
      }
      // Clear token from URL, switch to list view, and refresh
      setSearchParams({});
      setTokenDetails(null);
      await loadList();
    } catch (e: any) {
      if (e?.requiresSubscription) {
        // Persist the token so auto-accept fires after Stripe returns.
        try { sessionStorage.setItem("lr_pending_network_invite_token", tokenParam); } catch {}
        handleSubscriptionPrompt();
      } else {
        toast.error(e?.message || `Failed to ${action}`);
      }
    } finally {
      setTokenResponding(null);
    }
  };


  /* ── Guard ───────────────────────────────────────────────────── */
  if (!isProvider) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground p-6 text-center">
        Network invitations are managed from your LR Direct member account.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card px-3 sm:px-6 py-4 sm:py-5">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 shrink-0"
            onClick={() => navigate("/lr-direct/broker")}
          >
            <ArrowLeft className="h-4 w-4" /> Back to Dashboard
          </Button>
          <div className="h-6 w-px bg-border hidden sm:block" />
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-teal/10 shrink-0">
            <Inbox className="h-5 w-5 text-brand-teal" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-foreground">Network Invitations</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Shippers can invite you to their private broker network for first-look access to their freight.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* ── Token-mode card ─────────────────────────────────── */}
        {tokenParam && (
          <Card className="p-6 border-brand-teal/30">
            {tokenLoading ? (
              <div className="flex items-center gap-3 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Loading invitation…</span>
              </div>
            ) : tokenError ? (
              <div className="flex items-start gap-3 text-destructive">
                <XCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">Invitation unavailable</div>
                  <div className="text-sm">{tokenError}</div>
                </div>
              </div>
            ) : tokenDetails ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge className="bg-brand-teal/15 text-brand-teal border-0 text-[10px] uppercase font-bold gap-1">
                    <Mail className="h-3 w-3" /> Email Invitation
                  </Badge>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">From</div>
                  <div className="flex items-center gap-2 text-lg font-bold text-foreground">
                    <Building2 className="h-5 w-5 text-brand-teal" />
                    {tokenDetails.shipperCompanyName || "Shipper"}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Network: {tokenDetails.networkName}
                  </div>
                </div>
                {tokenDetails.message && (
                  <div className="rounded-md bg-muted/40 border border-border p-3 text-sm text-foreground">
                    "{tokenDetails.message}"
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <Shield className="h-3 w-3" />
                  Accepting lets this shipper share private loads with you before they go to the open market.
                </div>
                {profile?.email &&
                  profile.email.toLowerCase() !== tokenDetails.invitedEmail.toLowerCase() && (
                    <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                      Heads up: this invitation was sent to{" "}
                      <span className="font-semibold">{tokenDetails.invitedEmail}</span> but you're signed in as{" "}
                      <span className="font-semibold">{profile.email}</span>. To join this network, go to{" "}
                      <span className="font-semibold">My Network</span> → click{" "}
                      <span className="font-semibold">Find a Shipper</span> → add the network key → request to join.
                    </div>
                  )}

                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <Button
                    onClick={() => handleTokenRespond("accept")}
                    disabled={!!tokenResponding}
                    className="gap-2 w-full sm:w-auto h-11 sm:h-10"
                    style={{ backgroundColor: "hsl(var(--brand-teal))" }}
                  >
                    {tokenResponding === "accept" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Accept Invitation
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleTokenRespond("decline")}
                    disabled={!!tokenResponding}
                    className="gap-2 w-full sm:w-auto h-11 sm:h-10"
                  >
                    {tokenResponding === "decline" ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                    Decline
                  </Button>
                </div>
              </div>
            ) : null}
          </Card>
        )}

        {/* ── List of pending invites ─────────────────────────── */}
        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
              <Inbox className="h-4 w-4 text-brand-teal" /> Pending Invitations
            </h2>
            <Badge variant="secondary" className="text-[10px]">{invites.length}</Badge>
          </div>

          {loading ? (
            <div className="p-5 space-y-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : invites.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No pending invitations. When a shipper invites you, it will appear here.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {invites.map((inv) => {
                const r = responding[inv.id];
                return (
                  <li key={inv.id} className="p-5">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1.5">
                          <Building2 className="h-4 w-4 text-brand-teal" />
                          <span className="font-semibold text-foreground">
                            {inv.shipperCompanyName || "Shipper"}
                          </span>
                          <Badge className="bg-amber-500/15 text-amber-600 border-0 text-[10px] uppercase font-bold gap-1">
                            <Clock className="h-3 w-3" /> Pending
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mb-2">
                          Network: {inv.networkName || "Private Network"} · Sent {formatDate(inv.created_at)} ·
                          Expires {formatDate(inv.expires_at)}
                        </div>
                        {inv.message && (
                          <div className="rounded-md bg-muted/40 border border-border p-2.5 text-sm text-foreground italic">
                            "{inv.message}"
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:shrink-0">
                        <Button
                          size="sm"
                          onClick={() => handleListRespond(inv, "accept")}
                          disabled={!!r}
                          className="gap-1.5 w-full sm:w-auto h-11 sm:h-9"
                          style={{ backgroundColor: "hsl(var(--brand-teal))" }}
                        >
                          {r === "accept" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleListRespond(inv, "decline")}
                          disabled={!!r}
                          className="gap-1.5 w-full sm:w-auto h-11 sm:h-9"
                        >
                          {r === "decline" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
                          Decline
                        </Button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* ── My outgoing join-requests ──────────────────────── */}
        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
              <Building2 className="h-4 w-4 text-brand-teal" /> My Requests to Shippers
            </h2>
            <Button size="sm" variant="outline" onClick={() => navigate("/lr-direct/shippers")}>
              Find shippers
            </Button>
          </div>
          {myRequests.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              You haven't requested to join any shipper networks yet.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {myRequests.map((r) => (
                <li key={r.id} className="p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-foreground text-sm">{r.shipper_name || "Shipper"}</span>
                      <RequestStatusBadge status={r.status} />
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Requested {formatDate(r.requested_at)}
                      {r.decided_at ? ` · Decided ${formatDate(r.decided_at)}` : ""}
                    </div>
                    {r.message && (
                      <div className="mt-1.5 rounded-md bg-muted/40 border border-border p-2 text-xs text-foreground italic line-clamp-2">
                        "{r.message}"
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function RequestStatusBadge({ status }: { status: string }) {
  if (status === "pending") return <Badge className="bg-amber-500/15 text-amber-600 border-0 text-[10px] uppercase font-bold gap-1"><Clock className="h-3 w-3" />Pending</Badge>;
  if (status === "approved") return <Badge className="bg-emerald-500/15 text-emerald-600 border-0 text-[10px] uppercase font-bold gap-1"><CheckCircle2 className="h-3 w-3" />Approved</Badge>;
  if (status === "declined") return <Badge className="bg-muted text-muted-foreground border-0 text-[10px] uppercase font-bold gap-1"><XCircle className="h-3 w-3" />Declined</Badge>;
  return <Badge className="bg-muted text-muted-foreground border-0 text-[10px] uppercase font-bold">{status}</Badge>;
}
